// Doc 03 §3 — group layout modes for container/repeater nodes. The `layout`
// prop was declared but ignored; this util turns it into deterministic inline
// styles (+ one class per layout family for container-only CSS effects like
// break-inside and nth-child offsets). Positions are fixed by index, never
// random, so SSR and previews are pixel-stable.

import type { CSSProperties } from "react";

export interface GroupLayout {
  className?: string;
  style?: CSSProperties;
}

export interface GroupLayoutOptions {
  /**
   * Phone mode: the container renders below the phone breakpoint
   * (`PHONE_BREAKPOINT` in templateResponsive.ts). Multi-column and absolute
   * layouts collapse into compact, app-like stacks — 2-up grids at most, one
   * column for the polaroid/scatter posters, wrapping shelves — and the
   * matching `.menuraq-phone` CSS layer (app/globals.css) neutralises the
   * per-child transforms those layouts rely on at desktop size.
   */
  phone?: boolean;
  phoneColumns?: 1 | 2;
}

const GRID_RE = /^grid\((\d{1,3})\)$/;
const MASONRY_RE = /^masonry\((\d{1,3})\)$/;
const GAP = "8px";

export function resolveGroupLayout(layout: unknown, options?: GroupLayoutOptions): GroupLayout {
  if (typeof layout !== "string") return {};
  const mode = layout.trim().toLowerCase();
  if (!mode) return {};
  const phone = options?.phone ?? false;

  const gridMatch = mode.match(GRID_RE);
  if (gridMatch) {
    const requested = Math.max(1, Math.min(12, Number(gridMatch[1]) || 2));
    // Each template chooses one or two app-like phone columns. Avoid squeezing
    // three-column desktop cards into unreadable phone thumbnails.
    const columns = phone ? Math.min(requested, options?.phoneColumns ?? 1) : requested;
    return {
      style: { display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: GAP },
    };
  }

  const masonryMatch = mode.match(MASONRY_RE);
  if (masonryMatch) {
    const requested = Math.max(1, Math.min(4, Number(masonryMatch[1]) || 2));
    const columns = phone ? Math.min(requested, 2) : requested;
    return { className: "menuraq-masonry", style: { columns, columnGap: GAP } };
  }

  switch (mode) {
    case "row":
      return {
        style: { display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-start" },
      };
    case "column":
    case "stack":
      return { style: { display: "flex", flexDirection: "column" } };
    case "grid":
    case "two-column":
      return {
        style: {
          display: "grid",
          gridTemplateColumns: `repeat(${phone ? options?.phoneColumns ?? 1 : 2}, minmax(0, 1fr))`,
          gap: GAP,
        },
      };
    case "masonry":
      return { className: "menuraq-masonry", style: { columns: phone ? 1 : 2, columnGap: GAP } };
    case "shelf":
      // Doc 03a §9/§13 — a non-wrapping row of cut-outs standing on a line is
      // the whole point at desktop size; on a phone the same row overflows
      // from the third item on (Drinks Shelf), so it wraps into clusters.
      return phone
        ? {
            style: {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              gap: "16px",
              rowGap: "20px",
            },
          }
        : {
            style: {
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-around",
            },
          };
    case "zigzag":
      return {
        className: "menuraq-zigzag",
        style: phone
          ? { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px 16px" }
          : { display: "flex", flexWrap: "wrap", alignItems: "center" },
      };
    case "scatter":
      // Phone: the 6-slot transform cycle is unreadable at 360px — become a
      // static 2-up grid; `.menuraq-phone .menuraq-scatter > *` drops the
      // per-child transforms.
      return {
        className: "menuraq-scatter",
        style: phone
          ? {
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
              position: "relative",
              minHeight: 0,
            }
          : {},
      };
    case "scatter8":
      // Doc 03a §7 — Bold Bowl Poster. The generic `scatter` is a 6n transform
      // cycle; the poster needs a fixed 8-slot table (deterministic positions, so
      // a re-render is identical). Positions live in globals.css nth-child rules.
      // Phone: the absolute slot table (360px-wide children inside a ~260px
      // column) becomes a static 2-up grid; `.menuraq-phone .menuraq-scatter8 > *`
      // neutralises the absolute positioning.
      return {
        className: "menuraq-scatter8",
        style: phone
          ? {
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "16px",
              position: "relative",
              minHeight: 0,
            }
          : { position: "relative", minHeight: "1500px" },
      };
    case "polaroid":
      // Doc 03a §10 — Café Polaroid. A 3-column masonry whose cards tilt on a
      // fixed 4-step cycle (−3°, 2°, −1°, 3°) keyed to child order, never to RNG.
      // Phone: one column — a stack of full-width polaroids; 3 columns left
      // 48px-wide captions that bled across the whole card grid.
      return { className: "menuraq-polaroid", style: { columns: phone ? 1 : 3, columnGap: GAP } };
    case "split-alt":
      // Doc 03a §15 — Split Hero. Each category is a photo+list row; even rows
      // flip the photo to the right (`row-reverse`), so sides alternate by index.
      // Phone: `.menuraq-phone .menuraq-split-alt > *` stacks the row (photo
      // banner above the list) and neutralises the flip.
      return {
        className: "menuraq-split-alt",
        style: { display: "flex", flexDirection: "column", gap: phone ? "24px" : "48px" },
      };
  }

  return {};
}
